import { useMemo, useState } from 'react'
import { Check, Loader2, Phone, Search, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  useAddTeamMember,
  useAvailableTeamAgents,
  useRemoveTeamMember,
  useTeamGroupDetail,
  type TeamMemberType,
} from '@/api/teamGroups'

interface Props {
  open: boolean
  onClose: () => void
  teamId: number
  teamName: string
}

interface Ligne {
  id: number
  type: TeamMemberType
  name: string
  detail: string
  online: boolean
  membre: boolean
}

/**
 * Membres d'une équipe : une seule liste de tous les agents (web et téléphone),
 * une recherche, et sur chaque ligne un bouton qui dit ce qu'il fait : Ajouter
 * ou Retirer. Les membres sont en tête, cochés.
 */
export function TeamMembersDialog({ open, onClose, teamId, teamName }: Props) {
  const { t } = useTranslation()
  const { data: detail, isLoading: detailLoading } = useTeamGroupDetail(open ? teamId : null)
  const { data: available, isLoading: availLoading } = useAvailableTeamAgents(open)
  const addMember = useAddTeamMember(teamId)
  const removeMember = useRemoveTeamMember(teamId)
  const [recherche, setRecherche] = useState('')
  const [enCours, setEnCours] = useState<string | null>(null)

  const membres = detail?.members ?? []
  const idsMembres = useMemo(
    () => new Set(membres.map((m) => `${m.type}-${m.id}`)),
    [membres],
  )

  const lignes: Ligne[] = useMemo(() => {
    const web: Ligne[] = (available?.web_agents ?? []).map((a) => ({
      id: a.id, type: 'web', name: a.name, detail: a.email, online: a.status === 'online',
      membre: idsMembres.has(`web-${a.id}`),
    }))
    const phone: Ligne[] = (available?.phone_agents ?? []).map((a) => ({
      id: a.id, type: 'phone', name: a.name, detail: a.phone, online: false,
      membre: idsMembres.has(`phone-${a.id}`),
    }))
    const tout = [...web, ...phone]
    const q = recherche.trim().toLowerCase()
    const filtre = q ? tout.filter((l) => `${l.name} ${l.detail}`.toLowerCase().includes(q)) : tout
    // Membres d'abord, puis par nom.
    return filtre.sort((a, b) => Number(b.membre) - Number(a.membre) || a.name.localeCompare(b.name))
  }, [available, idsMembres, recherche])

  const basculer = async (l: Ligne) => {
    const cle = `${l.type}-${l.id}`
    setEnCours(cle)
    try {
      if (l.membre) {
        await removeMember.mutateAsync({ agent_id: l.id, type: l.type })
        toast.success(t('manager.team.groups.members.removed'))
      } else {
        await addMember.mutateAsync({ agent_id: l.id, type: l.type })
        toast.success(t('manager.team.groups.members.added'))
      }
    } catch {
      toast.error(t(l.membre ? 'manager.team.groups.members.removeError' : 'manager.team.groups.members.addError'))
    } finally {
      setEnCours(null)
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && !addMember.isPending && !removeMember.isPending) onClose()
  }

  const isLoading = detailLoading || availLoading

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('manager.team.groups.members.title', { name: teamName })}</DialogTitle>
          <DialogDescription>
            {t('manager.team.groups.members.count', { count: membres.length })}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder={t('manager.team.groups.members.search')}
            className="pl-9"
            autoFocus
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : lignes.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">{t('manager.team.groups.members.none')}</p>
        ) : (
          <ul className="max-h-[55vh] overflow-y-auto -mx-2 divide-y divide-slate-100 dark:divide-slate-800">
            {lignes.map((l) => {
              const cle = `${l.type}-${l.id}`
              const occupe = enCours === cle
              return (
                <li key={cle} className="flex items-center justify-between gap-3 py-2.5 px-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold',
                        l.membre
                          ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                      )}
                    >
                      {l.membre ? <Check className="h-4 w-4" /> : l.type === 'phone' ? <Phone className="h-3.5 w-3.5" /> : (l.name[0] || '?').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-900 dark:text-white truncate flex items-center gap-2">
                        {l.name}
                        {l.type === 'web' && l.online && (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title={t('manager.team.groups.members.online')} />
                        )}
                      </p>
                      <p className={cn('text-xs text-slate-400 dark:text-slate-500 truncate', l.type === 'phone' && 'font-mono')}>
                        {l.detail}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant={l.membre ? 'outline' : 'default'}
                    onClick={() => void basculer(l)}
                    disabled={enCours !== null}
                    className="flex-shrink-0 min-w-[96px]"
                  >
                    {occupe ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : l.membre ? (
                      t('manager.team.groups.members.remove')
                    ) : (
                      t('manager.team.groups.members.add')
                    )}
                  </Button>
                </li>
              )
            })}
          </ul>
        )}

        <DialogFooter className="items-center sm:justify-between gap-3">
          <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            {t('manager.team.groups.members.hint')}
          </p>
          <Button type="button" variant="outline" onClick={onClose} disabled={enCours !== null}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
